import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useForm } from "react-hook-form";
import { Payload } from "../types";
import { useReveal } from "../hooks/useReveal";
import emailjs from "@emailjs/browser";

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID!;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID!;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY!;

const contactInfo = [
	{
		icon: "fluent:mail-24-filled",
		label: "Email",
		value: "me@kimbohlovette.com",
		href: "mailto:me@kimbohlovette.com",
		color: "text-violet-400",
	},
	{
		icon: "fluent:call-24-filled",
		label: "Phone",
		value: "+237 654-115-922",
		href: "tel:+237654115922",
		color: "text-cyan-400",
	},
	{
		icon: "mdi:map-marker-outline",
		label: "Location",
		value: "Buea, Cameroon · Open to Relocate",
		href: null,
		color: "text-emerald-400",
	},
];

const socials = [
	{
		icon: "mdi:github",
		href: "https://www.github.com/kimbohlovette",
		label: "GitHub",
	},
	{
		icon: "ps:linkedin",
		href: "https://www.linkedin.com/in/kimbohlovette",
		label: "LinkedIn",
	},
	{
		icon: "mdi:twitter",
		href: "https://www.twitter.com/kimbohlovette",
		label: "Twitter",
	},
];

type ToastState = { show: boolean; success: boolean };

const GetIntouch = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [toast, setToast] = useState<ToastState>({
		show: false,
		success: false,
	});
	const ref1 = useReveal();
	const ref2 = useReveal();

	useEffect(() => {
		if (toast.show) {
			const t = setTimeout(
				() => setToast((s) => ({ ...s, show: false })),
				4000,
			);
			return () => clearTimeout(t);
		}
	}, [toast.show]);

	const { register, handleSubmit, reset } = useForm<Payload>();

	const onSubmit = (data: Payload) => {
		setIsLoading(true);
		emailjs
			.send(
				EMAILJS_SERVICE_ID,
				EMAILJS_TEMPLATE_ID,
				{
					from_name: data.name,
					contact: data.contact,
					message: data.message,
				},
				{ publicKey: EMAILJS_PUBLIC_KEY },
			)
			.then(() => {
				setToast({ show: true, success: true });
				reset();
			})
			.catch(() => setToast({ show: true, success: false }))
			.finally(() => setIsLoading(false));
	};

	return (
		<section className="max-w-6xl mx-auto px-6">
			{/* Toast */}
			<div
				className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border text-sm font-medium transition-all duration-500 ${
					toast.show
						? "opacity-100 translate-y-0"
						: "opacity-0 translate-y-4 pointer-events-none"
				} ${
					toast.success
						? "bg-emerald-950/90 border-emerald-700/60 text-emerald-300"
						: "bg-red-950/90 border-red-700/60 text-red-300"
				}`}
			>
				<Icon
					icon={
						toast.success ? "mdi:check-circle" : "mdi:alert-circle"
					}
					className="text-xl flex-shrink-0"
				/>
				{toast.success
					? "Message sent! I'll get back to you soon."
					: "Failed to send. Please try again."}
				<button
					onClick={() => setToast((s) => ({ ...s, show: false }))}
					className="ml-1 opacity-60 hover:opacity-100 transition-opacity"
				>
					<Icon icon="mdi:close" />
				</button>
			</div>

			<div className="grid md:grid-cols-2 gap-8">
				{/* Info */}
				<div ref={ref1} className="reveal flex flex-col gap-8">
					<p className="text-slate-400 leading-relaxed">
						I'm open to new opportunities, collaborations, and
						interesting projects. Whether you have a question or
						just want to say hi — my inbox is always open.
					</p>

					<div className="flex flex-col gap-4">
						{contactInfo.map((c) => (
							<div
								key={c.label}
								className="flex items-center gap-4 group"
							>
								<div className="w-11 h-11 rounded-xl glass-card border border-slate-700 flex items-center justify-center flex-shrink-0 group-hover:border-accent/40 transition-colors duration-500">
									<Icon
										icon={c.icon}
										className={`${c.color} text-lg`}
									/>
								</div>
								<div>
									<p className="text-xs text-slate-600 font-semibold uppercase tracking-wide">
										{c.label}
									</p>
									{c.href ? (
										<a
											href={c.href}
											className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
										>
											{c.value}
										</a>
									) : (
										<p className="text-slate-300 text-sm font-medium">
											{c.value}
										</p>
									)}
								</div>
							</div>
						))}
					</div>

					<div>
						<p className="text-xs text-slate-600 font-semibold uppercase tracking-wide mb-3">
							Social
						</p>
						<div className="flex gap-3">
							{socials.map((s) => (
								<a
									key={s.label}
									href={s.href}
									target="_blank"
									rel="noreferrer"
									aria-label={s.label}
									className="w-11 h-11 rounded-xl glass-card border border-slate-700 text-slate-400 flex items-center justify-center hover:border-accent/50 hover:text-white hover:bg-accent/10 transition-all duration-500 text-lg"
								>
									<Icon icon={s.icon} />
								</a>
							))}
						</div>
					</div>
				</div>

				{/* Form */}
				<form
					ref={ref2 as any}
					onSubmit={handleSubmit(onSubmit)}
					className="reveal reveal-delay-2 section-card flex flex-col gap-5"
				>
					<div>
						<label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
							Full Name
						</label>
						<input
							type="text"
							{...register("name")}
							required
							disabled={isLoading}
							placeholder="John Doe"
							className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 transition-all duration-500 text-sm"
						/>
					</div>
					<div>
						<label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
							Email or Phone
						</label>
						<input
							type="text"
							{...register("contact")}
							required
							disabled={isLoading}
							placeholder="john@example.com or +1 234 567 8900"
							className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 transition-all duration-500 text-sm"
						/>
					</div>
					<div>
						<label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
							Message
						</label>
						<textarea
							{...register("message")}
							required
							disabled={isLoading}
							rows={4}
							placeholder="Tell me about your project..."
							className="w-full px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700 text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 transition-all duration-500 text-sm resize-none"
						/>
					</div>
					<button
						type="submit"
						disabled={isLoading}
						className="btn-primary justify-center mt-1 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:transform-none disabled:hover:shadow-none relative overflow-hidden"
					>
						{isLoading ? (
							<>
								<Icon
									icon="eos-icons:three-dots-loading"
									className="text-xl"
								/>
								<span>Sending...</span>
							</>
						) : (
							<>
								<Icon icon="quill:send" className="text-base" />
								<span>Send message</span>
							</>
						)}
					</button>
				</form>
			</div>
		</section>
	);
};

export default GetIntouch;
